import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Shield, Loader2 } from 'lucide-react';
import { MFAEnrollDialog } from '@/components/MFAEnrollDialog';
import { MFAVerifyStep } from '@/components/MFAVerifyStep';
import { useAuth } from '@/contexts/AuthContext';

type GateState = 'checking' | 'ok' | 'must_enroll' | 'must_verify';

interface Props {
  onSatisfied: () => void;
}

/**
 * Server-enforced MFA gate. Reads DB function `user_requires_mfa` (which
 * combines the org-wide toggle in system_settings with the caller's roles)
 * and blocks app access until the user has a verified TOTP factor at aal2.
 */
export function MFARequiredGate({ onSatisfied }: Props) {
  const { logout } = useAuth();
  const [state, setState] = useState<GateState>('checking');
  const [enrollOpen, setEnrollOpen] = useState(false);

  const check = async () => {
    setState('checking');
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData.user?.id;
    if (!uid) {
      onSatisfied();
      return;
    }

    // Server-side requirement check
    const { data: required, error: reqErr } = await supabase.rpc('user_requires_mfa', {
      _user_id: uid,
    });
    if (reqErr) {
      // Fail closed only if we genuinely can't determine; otherwise let user in
      console.error('user_requires_mfa error', reqErr);
      onSatisfied();
      return;
    }
    if (!required) {
      onSatisfied();
      return;
    }

    const { data: factors } = await supabase.auth.mfa.listFactors();
    const hasVerifiedTotp = (factors?.totp ?? []).some((f) => f.status === 'verified');
    if (!hasVerifiedTotp) {
      setState('must_enroll');
      setEnrollOpen(true);
      return;
    }

    const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (aal?.currentLevel === 'aal2') {
      onSatisfied();
      return;
    }
    setState('must_verify');
  };

  useEffect(() => {
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state === 'checking') {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (state === 'must_verify') {
    return <MFAVerifyStep onVerified={check} onCancel={logout} />;
  }

  // must_enroll
  return (
    <>
      <div className="min-h-screen w-full flex items-center justify-center bg-background p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Two-Factor Authentication Required
            </CardTitle>
            <CardDescription>
              Two-factor authentication is required for every account. Set up an authenticator
              app (Google Authenticator, Microsoft Authenticator, Authy, 1Password, etc.) to continue.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button className="w-full" onClick={() => setEnrollOpen(true)}>
              Set Up Two-Factor Authentication
            </Button>
            <Button variant="ghost" className="w-full" onClick={logout}>
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
      <MFAEnrollDialog
        open={enrollOpen}
        onOpenChange={setEnrollOpen}
        onEnrolled={() => {
          setEnrollOpen(false);
          check();
        }}
      />
    </>
  );
}
